import {
  useAllCategoriesQuery,
  useAllCategoryGroupsQuery,
  useBindingsQuery,
  useCashFlowByCategoryQuery,
  useCashFlowByPayeeQuery,
  usePayrollSummaryQuery,
  useUnallocatedCashFlowQuery,
} from '@transactions/graphql';
import {parseDate} from 'common/date';
import {ArrowLeft} from 'lucide-react';
import * as React from 'react';
import {Link} from 'react-router-dom';
import {
  atTrueScale,
  buildPayeeFlow,
  buildTieredFlow,
  formatShare,
} from 'src/AnalyticsFlowView/flowModel';
import {FlowSankey} from 'src/AnalyticsFlowView/FlowSankey';
import {TieredSankey} from 'src/AnalyticsFlowView/TieredSankey';
import {Button} from 'src/Button';
import {
  DateFilterSelect,
  type DateFilterState,
} from 'src/components/DateFilterSelect';
import {useDayKey} from 'src/lib/useDayKey';
import {cn} from 'src/lib/utils';

/**
 * Node labels stay compact ($105k) while the caption below the chart uses
 * exact dollars — precision belongs in prose, not crammed beside a ribbon.
 */
const formatCompact = (cents: number): string => {
  const dollars = Math.abs(cents) / 100;
  if (dollars >= 100_000) {
    return `$${Math.round(dollars / 1000).toLocaleString('en-US')}k`;
  }
  if (dollars >= 10_000) {
    return `$${(dollars / 1000).toFixed(1)}k`;
  }
  return dollars.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
};

const formatExact = (cents: number): string =>
  (Math.abs(cents) / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

/**
 * The window is either one of two relative presets (which follow the
 * calendar day) or an absolute range from the shared date filter — whose
 * empty state means "all time", same as everywhere else it is used.
 */
type WindowMode =
  | {kind: '12mo'}
  | {kind: 'ytd'}
  | {kind: 'absolute'; value: DateFilterState};

const RELATIVE_PRESETS: Array<{kind: '12mo' | 'ytd'; label: string}> = [
  {kind: '12mo', label: 'Trailing 12 months'},
  {kind: 'ytd', label: 'This year'},
];

const resolveWindow = (
  mode: WindowMode,
  day: string,
): {startDate: string; endDate: string} => {
  const now = parseDate(day);
  const iso = (d: typeof now): string => d.toISODate() ?? '';
  switch (mode.kind) {
    case '12mo':
      return {
        startDate: iso(now.minus({years: 1}).plus({days: 1})),
        endDate: iso(now),
      };
    case 'ytd':
      return {startDate: iso(now.startOf('year')), endDate: iso(now)};
    case 'absolute':
      return {
        // Far enough back that even hand-entered historical records
        // qualify; an open end runs through today.
        startDate: mode.value.startDate ?? '1900-01-01',
        endDate: mode.value.endDate ?? iso(now),
      };
  }
};

export const AnalyticsFlowView = () => {
  const dayKey = useDayKey();
  const [windowMode, setWindowMode] = React.useState<WindowMode>({
    kind: '12mo',
  });
  const activeWindow = React.useMemo(
    () => resolveWindow(windowMode, dayKey),
    [windowMode, dayKey],
  );
  const [drillCategoryId, setDrillCategoryId] = React.useState<string | null>(
    null,
  );
  const [trueScale, setTrueScale] = React.useState(false);
  const [expandAll, setExpandAll] = React.useState(false);

  const {
    data: categoriesData,
    loading: categoriesLoading,
    error: categoriesError,
  } = useAllCategoriesQuery({
    // Same freshness contract as the aggregates below: a category created
    // or renamed in another session should label this map after a revisit.
    fetchPolicy: 'cache-and-network',
  });
  const {
    data: categoryGroupsData,
    loading: categoryGroupsLoading,
    error: categoryGroupsError,
  } = useAllCategoryGroupsQuery({fetchPolicy: 'cache-and-network'});
  const {
    data: flowData,
    loading: flowLoading,
    error: flowError,
  } = useCashFlowByCategoryQuery({
    variables: {startDate: activeWindow.startDate, endDate: activeWindow.endDate},
    // Aggregates go stale as soon as a payment is edited or synced anywhere
    // else in the app; nothing evicts them, so revalidate on every visit
    // while still painting the cached result immediately.
    fetchPolicy: 'cache-and-network',
  });
  const {
    data: payrollData,
    loading: payrollLoading,
    error: payrollError,
  } = usePayrollSummaryQuery({
    variables: {startDate: activeWindow.startDate, endDate: activeWindow.endDate},
    fetchPolicy: 'cache-and-network',
  });
  // The taxes band needs to know which category is taxes; the binding is
  // user-set on the Defaults page (taxesCategory slot).
  const {
    data: bindingsData,
    loading: bindingsLoading,
    error: bindingsError,
  } = useBindingsQuery({fetchPolicy: 'cache-and-network'});
  // Raw rows nothing has consumed yet. The rollups above exclude them, so
  // without this their outflows would read as Saving with no indicator.
  const {
    data: unallocatedData,
    loading: unallocatedLoading,
    error: unallocatedError,
  } = useUnallocatedCashFlowQuery({
    variables: {startDate: activeWindow.startDate, endDate: activeWindow.endDate},
    fetchPolicy: 'cache-and-network',
  });
  const {
    data: payeeData,
    loading: payeeLoading,
    error: payeeError,
  } = useCashFlowByPayeeQuery({
    variables: {
      startDate: activeWindow.startDate,
      endDate: activeWindow.endDate,
      categoryId: drillCategoryId ?? '',
    },
    skip: drillCategoryId == null,
    fetchPolicy: 'cache-and-network',
  });

  const categoriesById = React.useMemo(
    () =>
      new Map(
        (categoriesData?.allCategories ?? []).map((category) => [
          category._id,
          category,
        ]),
      ),
    [categoriesData],
  );

  const payrollSummary = payrollData?.payrollSummary ?? null;
  const unallocated = unallocatedData?.unallocatedCashFlow ?? null;
  // A binding can dangle (category deleted after binding); treat it as
  // unset so withholding renders as the standalone band instead of an
  // "Unknown category" node with a dead drill. The Defaults page flags the
  // dangling row for cleanup.
  const boundCategory = (key: string): string | null => {
    const id =
      bindingsData?.bindings.find((binding) => binding.key === key)
        ?.entityId ?? null;
    return id != null && categoriesById.has(id) ? id : null;
  };
  const taxesCategoryId = boundCategory('taxesCategory');
  // Binding the same category to both slots is contradictory (its cash rows
  // cannot be both paycheck deposits and tax payments); taxes wins, because
  // the withholding's destination is the better-defined half.
  const boundPaycheckId = boundCategory('paycheckCategory');
  const paycheckCategoryId =
    boundPaycheckId != null && boundPaycheckId === taxesCategoryId
      ? null
      : boundPaycheckId;
  // Same policy down the chain: taxes > paycheck > retirement. Without
  // this, the retirement pin would extract the shared category before the
  // taxes tier could net against it, misreporting a refund as retirement
  // income and overstating taxes.
  const boundRetirementId = boundCategory('retirementCategory');
  const retirementCategoryId =
    boundRetirementId != null &&
    (boundRetirementId === taxesCategoryId ||
      boundRetirementId === paycheckCategoryId)
      ? null
      : boundRetirementId;

  const overview = React.useMemo(
    () =>
      buildTieredFlow({
        groups: (flowData?.cashFlowByCategory ?? []).map((group) => ({
          key: group.key ?? null,
          net: group.net,
        })),
        categoriesById,
        categoryGroups: (categoryGroupsData?.allCategoryGroups ?? []).map(
          (group) => ({
            _id: group._id,
            name: group.name,
            categoryIds: group.categoryIds,
            color: group.color ?? null,
          }),
        ),
        // Always passed: the bindings shape the map (taxes tier from cash
        // rows, retirement pin) even before any payroll is imported.
        payroll: {
          taxesWithheld: payrollSummary?.taxesWithheld ?? 0,
          taxesCategoryId,
          paycheckCategoryId,
          retirementCategoryId,
        },
        unreviewed:
          unallocated != null
            ? {net: unallocated.net, count: unallocated.count}
            : undefined,
        maxNodes: expandAll ? Number.POSITIVE_INFINITY : undefined,
      }),
    [
      flowData,
      categoriesById,
      categoryGroupsData,
      payrollSummary,
      unallocated,
      taxesCategoryId,
      paycheckCategoryId,
      retirementCategoryId,
      expandAll,
    ],
  );
  const drawnOverview = React.useMemo(
    () => (trueScale ? atTrueScale(overview) : overview),
    [overview, trueScale],
  );

  const drill = React.useMemo(() => {
    if (drillCategoryId == null) {
      return null;
    }
    const groups = (payeeData?.cashFlowByPayee ?? []).map((group) => ({
      key: group.key ?? null,
      net: group.net,
    }));
    // Drills must not lose the withheld share the overview merged in: on
    // the taxes drill it joins as an outflow, on the paycheck drill as an
    // inflow (the pay that never reached a payee). The paycheck row only
    // appears when the overview actually merged — a net-negative window
    // keeps the category on the spend side with the standalone band, and
    // its drill must reconcile with that node instead.
    const withheld = payrollSummary?.taxesWithheld ?? 0;
    const paycheckMerged =
      drillCategoryId === paycheckCategoryId &&
      (flowData?.cashFlowByCategory.find(
        (group) => group.key === paycheckCategoryId,
      )?.net ?? 0) > 0;
    const extra =
      withheld > 0 && drillCategoryId === taxesCategoryId
        ? [{key: 'Payroll withholding', net: -withheld}]
        : withheld > 0 && paycheckMerged
          ? [{key: 'Withheld pay', net: withheld}]
          : [];
    return buildPayeeFlow({groups: [...groups, ...extra]});
  }, [
    drillCategoryId,
    payeeData,
    flowData,
    taxesCategoryId,
    paycheckCategoryId,
    payrollSummary,
  ]);

  const drillCategoryName =
    drillCategoryId != null
      ? (categoriesById.get(drillCategoryId)?.name ?? 'Category')
      : null;
  const {totals} = overview;
  // Caption percentages share one denominator: the money that actually
  // flowed this window (income, plus any savings draw, plus unreviewed
  // inflows — everything the spine carries).
  const captionBase =
    totals.income + totals.fromSavings + Math.max(0, totals.unreviewed);
  const withShare = (value: number): string => {
    const share = formatShare(value, captionBase);
    return share != null ? ` (${share})` : '';
  };
  // Metadata gates rendering too: without the categories, every label is
  // unknown and — worse — windfall categories read as regular income. And a
  // failed query must not render as a plausible empty window.
  // The rollup gates both modes: the drill derives its synthetic rows and
  // header framing from it, so a payee response arriving ahead of a stale
  // or failed rollup must not render.
  const loading =
    (categoriesLoading && categoriesData == null) ||
    (categoryGroupsLoading && categoryGroupsData == null) ||
    (payrollLoading && payrollData == null) ||
    (bindingsLoading && bindingsData == null) ||
    (flowLoading && flowData == null) ||
    (unallocatedLoading && unallocatedData == null) ||
    (drillCategoryId != null && payeeLoading && payeeData == null);
  const error =
    categoriesError ??
    categoryGroupsError ??
    payrollError ??
    bindingsError ??
    flowError ??
    unallocatedError ??
    (drillCategoryId != null ? payeeError : undefined);

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-foreground text-xl font-semibold">
              {drillCategoryName == null ? (
                'Cash flow'
              ) : (
                <span className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => setDrillCategoryId(null)}
                    aria-label="Back to all categories"
                  >
                    <ArrowLeft className="size-4" />
                  </Button>
                  {drillCategoryName} · by payee
                </span>
              )}
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {RELATIVE_PRESETS.map((preset) => (
              <button
                key={preset.kind}
                type="button"
                onClick={() => setWindowMode({kind: preset.kind})}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                  windowMode.kind === preset.kind
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border text-muted-foreground hover:text-foreground',
                )}
              >
                {preset.label}
              </button>
            ))}
            {/* Years, months, custom ranges, and All time — the same filter
                the transaction lists use, so a year is one click. */}
            <DateFilterSelect
              value={
                windowMode.kind === 'absolute'
                  ? windowMode.value
                  : {startDate: null, endDate: null}
              }
              onChange={(value) => setWindowMode({kind: 'absolute', value})}
              emptyMeaning={
                windowMode.kind === 'absolute' ? 'all-time' : 'unset'
              }
            />
          </div>
        </div>

        {error && (
          <div className="text-destructive py-12 text-center text-sm">
            Couldn't load cash flow: {error.message}
          </div>
        )}

        {!error && loading && (
          <div className="text-muted-foreground py-12 text-center text-sm">
            Loading…
          </div>
        )}

        {!error && !loading && drill == null && (
          <>
            <TieredSankey
              model={drawnOverview}
              onSelectCategory={setDrillCategoryId}
              formatValue={formatCompact}
            />
            <p className="text-muted-foreground text-xs">
              {payrollSummary != null && payrollSummary.taxesWithheld > 0 && (
                <>
                  Paychecks deposited {formatExact(payrollSummary.net)} of{' '}
                  {formatExact(payrollSummary.gross)} gross;{' '}
                  {formatExact(payrollSummary.taxesWithheld)} of the difference
                  was tax withholding, shown here.{' '}
                </>
              )}
              Of {formatExact(totals.income)} in
              {totals.fromSavings > 0 &&
                ` plus ${formatExact(totals.fromSavings)} from savings`}
              {totals.unreviewed > 0 &&
                ` plus ${formatExact(totals.unreviewed)} not yet reviewed`}
              :
              {totals.taxes > 0 &&
                ` ${formatExact(totals.taxes)}${withShare(totals.taxes)} to taxes,`}{' '}
              {formatExact(totals.spending)}
              {withShare(totals.spending)} spent
              {totals.unreviewed < 0 &&
                `, ${formatExact(totals.unreviewed)}${withShare(-totals.unreviewed)} not yet reviewed`}
              {totals.saving > 0 &&
                `, ${formatExact(totals.saving)}${withShare(totals.saving)} saved`}
              .{' '}
              {/* The band is a to-do: name the rows behind it and where to
                  clear them. Foreign rows have no USD value to draw until
                  allocated, so they are counted here but never drawn. */}
              {(totals.unreviewedCount > 0 ||
                (unallocated?.foreignCount ?? 0) > 0) && (
                <>
                  “Unreviewed” nets {totals.unreviewedCount}{' '}
                  {totals.unreviewedCount === 1
                    ? 'raw transaction'
                    : 'raw transactions'}{' '}
                  no transaction has consumed yet
                  {(unallocated?.foreignCount ?? 0) > 0 &&
                    `, and ${unallocated?.foreignCount} more in other currencies are not drawn until allocated`}
                  ; it may hide transfer legs, so it is not counted as
                  spending or income.{' '}
                  {/* Carry the window: without start/end the review list
                      floors at the backlog cutoff and would hide the older
                      rows this count includes. */}
                  <Link
                    to={`/ledger?layer=activity&view=review&start=${activeWindow.startDate}&end=${activeWindow.endDate}`}
                    className="text-foreground underline decoration-dotted underline-offset-2"
                  >
                    Review them
                  </Link>
                  .{' '}
                </>
              )}
              {overview.scaleBreak != null &&
                (trueScale
                  ? 'Drawn at true scale. '
                  : `Scale break: ${overview.scaleBreak.label} and “Saving” are drawn ${formatExact(overview.scaleBreak.hidden)} shorter so smaller flows stay readable — labels show true amounts. `)}
              {overview.scaleBreak != null && (
                <button
                  type="button"
                  className="text-foreground underline decoration-dotted underline-offset-2"
                  onClick={() => setTrueScale(!trueScale)}
                >
                  {trueScale ? 'Restore scale break' : 'Show true scale'}
                </button>
              )}{' '}
              <button
                type="button"
                className="text-foreground underline decoration-dotted underline-offset-2"
                onClick={() => setExpandAll(!expandAll)}
              >
                {expandAll ? 'Fold small categories' : 'Show all categories'}
              </button>
            </p>
          </>
        )}

        {!error && !loading && drill != null && (
          <FlowSankey
            sources={drill.sources}
            dests={drill.dests}
            formatValue={formatCompact}
          />
        )}
    </div>
  );
};
